<template>
    <section class="section referee-fights-app-page">
        <div class="container-fluid">
            <div class="columns">
                <div class="column">
                    <h1 class="title">{{ translate("page.start_fights.title") }}</h1>
                </div>
            </div>

            <div class="columns" v-if="!matIsSelected">
                <div class="column">
                    <RefereeMatSelect ref="refereeMatSelect" :event-id="eventId" @matIdSelected="onMatIdSelected" />
                </div>
            </div>

            <div class="columns" v-else-if="initDone && matIsSelected">
                <div class="column is-12">
                    <div class="mb-3 is-flex is-align-items-center">
                        <span class="mr-2 is-description">{{ translate("page.start_fights.managed_mat") }}</span>
                        <span class="tag is-info" v-if="matId > 0">{{ matName }}</span>
                        <strong class="empty-mat" v-else>
                            {{ translate(matName, true) }}
                        </strong>
                        <button class="ml-3 button is-small is-text" @click="changeSelectedMat">
                            <span class="icon">
                                <i class="fa fa-edit" />
                            </span>
                            <span>{{ translate("common.change") }}</span>
                        </button>
                    </div>

                    <div class="buttons hidden-print">
                        <a
                            :href="eventAdmin().startFirstFightPage(eventId, matId)"
                            class="button is-primary"
                            :class="{ 'is-disabled': !fights.length }"
                        >
                            <span class="icon">
                                <i class="fa fa-play" />
                            </span>
                            <span>{{ translate("page.start_fights.start_first_fight") }}</span>
                        </a>

                        <button class="button" @click="openFinishedFights">
                            <span class="icon">🏁</span>

                            <span>{{ translate("page.start_fights.show_finished") }}</span>
                        </button>

                        <LoadingButton class="button" @click="refreshFights" :is-loading="refreshInProgress">
                            <span class="icon">
                                <i class="fa fa-sync" />
                            </span>
                            <span>{{ translate("common.refresh") }}</span>
                        </LoadingButton>

                        <MatScheduleDelayStatus
                            v-if="matScheduleDelay"
                            :schedule-delay="matScheduleDelay"
                            :event-timezone="eventTimezone"
                        />
                    </div>

                    <table class="table is-condensed is-hoverable is-fullwidth" v-if="fights.length">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>{{ translate("common.category") }}</th>
                                <th>{{ translate("common.round") }}</th>
                                <th>{{ translate("page.start_fights.competitor") }} #1</th>
                                <th>{{ translate("page.start_fights.competitor") }} #2</th>
                                <th>{{ translate("page.start_fights.fight_time") }}</th>
                                <th class="hidden-print" />
                            </tr>
                        </thead>

                        <tbody>
                            <FightTableRow
                                :selection-mode="false"
                                v-for="(fight, index) in fights"
                                :fight="fight"
                                :index="index"
                                :key="fight.id"
                                :show-duplicated-pair="false"
                            >
                                <template v-slot:afterCells>
                                    <td>
                                        {{ fight.fightTime.time }}
                                    </td>
                                    <td class="hidden-print">
                                        <a
                                            v-if="isPlayable(index, fight)"
                                            :href="eventAdmin().startIndividualFightPage(eventId, fight.id)"
                                            class="button is-small start-fight-button"
                                        >
                                            <span class="icon">
                                                <i class="fa fa-play" />
                                            </span>
                                            <span>{{ translate("scoreboard.start") }}</span>
                                        </a>
                                    </td>
                                </template>
                            </FightTableRow>
                        </tbody>
                    </table>
                    <div class="columns is-centered" v-else>
                        <div class="column is-6">
                            <Notification class="is-success">
                                <span class="icon"> 🏁 </span>
                                <span>{{ translate("page.start_fights.empty_queue") }}</span>
                            </Notification>
                        </div>
                    </div>
                </div>
            </div>
            <div class="columns is-centered" v-else>
                <LoadingSpinner2 class="has-added-margin" />
            </div>

            <FinishedFightsForMatModal
                ref="finishedFightsModal"
                :event-id="eventId"
                :event-timezone="eventTimezone"
                :mat-id="matId"
                @fightReverted="onFightReverted"
            />
        </div>
    </section>
</template>

<script setup lang="ts">
    import { computed, nextTick, onMounted, ref } from "vue";
    import translatorMixin from "../Common/translatorMixin";
    import FightTableRow from "./FightTableRow.vue";
    import FinishedFightsForMatModal from "./FinishedFightsForMatModal.vue";
    import store from "../Common/store";
    import RefereeMatSelect from "./RefereeMatSelect.vue";
    import LoadingButton from "../Common/LoadingButton.vue";
    import LoadingSpinner2 from "../Common/LoadingSpinner2.vue";
    import Notification from "../Common/Notification.vue";
    import urls from "../Urls/urls";
    import { handleError } from "../Common/errorHandler";
    import matsApi, { MatScheduleDelay } from "../Api/mats";
    import MatScheduleDelayStatus from "./MatScheduleDelayStatus.vue";
    import fightsApi from "../Api/fights";
    import fightsQueueStatusApi from "../Api/fightQueueStatus";
    import { isQueueBreak, OrganizerComplexFight } from "../Common/Fights/types";

    const { translate } = translatorMixin.methods;
    const { eventAdmin } = urls;

    const props = defineProps<{
        eventId: number;
        eventTimezone: string;
    }>();

    const initDone = ref(false);

    // GETTING FIGHTS DATA

    const fights = ref<OrganizerComplexFight[]>([]);
    const firstBreakIndex = ref(-1);
    const refreshInProgress = ref(false);

    function clearQueueStatusForMat() {
        return fightsQueueStatusApi.removeStatusForMat(props.eventId, matId.value);
    }

    function fetchFights() {
        return fightsApi.getIncomingFights(props.eventId, matId.value).then(fightsCollection => {
            fights.value = fightsCollection.result;
            firstBreakIndex.value = fightsCollection.result.findIndex(fight => isQueueBreak(fight));
        });
    }

    // Schedule delay is informational only: it is fetched in the background, never blocks the fights list
    // and stays hidden when unavailable (no planned version, empty queue) or when the request fails.
    const matScheduleDelay = ref<MatScheduleDelay | null>(null);

    function fetchMatScheduleDelay() {
        const requestedMatId = matId.value;

        matsApi
            .getScheduleDelay(props.eventId, requestedMatId)
            .then(scheduleDelay => {
                if (requestedMatId === matId.value) {
                    matScheduleDelay.value = scheduleDelay.expectedEndTime && scheduleDelay.actualEndTime ? scheduleDelay : null;
                }
            })
            .catch(() => {
                if (requestedMatId === matId.value) {
                    matScheduleDelay.value = null;
                }
            });
    }

    const refreshFights = () => {
        refreshInProgress.value = true;
        fetchMatScheduleDelay();
        return Promise.all([clearQueueStatusForMat(), fetchFights()])
            .catch(handleError)
            .finally(() => {
                refreshInProgress.value = false;
            });
    };

    // CHOOSING MAT TO PLAY FIGHTS ON

    const refereeMatSelect = ref<InstanceType<typeof RefereeMatSelect> | null>(null);

    const matId = ref(0);
    const showMatSelection = ref(true);
    const matName = ref("");

    const matIsSelected = computed(() => matId.value > 0);

    function getSelectedMatKey(eventId) {
        return `selectedMatForEvent_${eventId}`;
    }

    function selectMat(matIdToSelect) {
        matId.value = parseInt(matIdToSelect);
        showMatSelection.value = false;
        store.set(getSelectedMatKey(props.eventId), matId.value);
        initializeFightRefreshingForSelectedMat();
    }

    function resetMatSelection() {
        store.delete(getSelectedMatKey(props.eventId));
        initDone.value = false;
        matId.value = 0;
        matName.value = "";
        matScheduleDelay.value = null;
        showMatSelection.value = true;

        nextTick(() => {
            refereeMatSelect.value?.init();
        });
    }

    function initializeFightRefreshingForSelectedMat() {
        if (!matIsSelected.value) {
            return resetMatSelection();
        }

        return matsApi
            .getSingleMat(props.eventId, matId.value)
            .then(mat => (matName.value = mat.name))
            .then(() => {
                return refreshFights();
            })
            .then(() => {
                initDone.value = true;
            })
            .catch(err => {
                handleError(err);
                alert(`Error during fetching mat. Event id = ${props.eventId}, mat id = ${matId.value}, details = ${err.message}`);
                resetMatSelection();
            });
    }

    function onMatIdSelected(selectedMatId) {
        selectMat(selectedMatId);
    }

    function changeSelectedMat() {
        resetMatSelection();
    }

    function getStoredMatId() {
        return parseInt(store.get(getSelectedMatKey(props.eventId)) ?? "0");
    }

    onMounted(() => {
        const storedMatId = getStoredMatId();
        if (storedMatId > 0) {
            selectMat(storedMatId);
        } else {
            resetMatSelection();
        }
    });

    // FINISHED FIGHTS HANDLING

    const finishedFightsModal = ref<InstanceType<typeof FinishedFightsForMatModal> | null>(null);

    function openFinishedFights() {
        finishedFightsModal.value?.open(matId.value);
    }

    function onFightReverted() {
        return refreshFights();
    }

    function isPlayable(index: number, fight: OrganizerComplexFight) {
        if (!fight.isReadyToPlay) {
            return false;
        }

        // Don't allow starting fights after a queue break
        if (firstBreakIndex.value !== -1 && index > firstBreakIndex.value) {
            return false;
        }

        return true;
    }
</script>

<style scoped>
    .empty-mat {
        color: red;
    }
</style>
